import { getAccessToken } from './authService';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface OnePieceCardOwnership {
  userId: string;
  cardId: string;
  setId: string;
  owned: boolean;
  quantity: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  salePrice?: number;
  saleDate?: string;
  notes?: string;
  location?: string;
  language?: string;
  disposition?: 'sold' | 'traded' | 'gifted' | null;
  tradedFor?: string;
  justTcgId?: string;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  inCollection?: boolean;
  isWishlist?: boolean;
  isFavorite?: boolean;
  links?: string[];
  createdAt?: string;
  updatedAt?: string;
  marketPrice?: number;
  currentPrice?: number;
}

export interface OnePieceCardOwnershipInput {
  setId: string;
  owned: boolean;
  quantity?: number;
  condition?: string;
  purchasePrice?: number;
  purchaseDate?: string;
  salePrice?: number;
  saleDate?: string;
  notes?: string;
  location?: string;
  language?: string;
  disposition?: 'sold' | 'traded' | 'gifted' | null;
  tradedFor?: string;
  justTcgId?: string;
  gradingCompany?: string;
  gradingGrade?: string;
  gradingId?: string;
  isGrading?: boolean;
  inCollection?: boolean;
  isWishlist?: boolean;
  isFavorite?: boolean;
  links?: string[];
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function getUserOnePieceCardOwnerships(
  setId?: string,
  includePricing: boolean = false,
  source: 'master' | 'collection' = 'master',
): Promise<OnePieceCardOwnership[]> {
  try {
    const headers = await getAuthHeaders();
    let url = `${API_BASE_URL}/api/onepiece/ownership?`;
    
    if (setId) url += `setId=${setId}&`;
    if (includePricing) url += `includePricing=true&`;
    
    // Remove trailing & or ? if empty (though ? is fine if params follow)
    // Cleaner:
    const params = new URLSearchParams();
    if (setId) params.append('setId', setId);
    if (includePricing) params.append('includePricing', 'true');
    if (source) params.append('source', source);
    
    url = `${API_BASE_URL}/api/onepiece/ownership?${params.toString()}`;

    console.log('[getUserOnePieceCardOwnerships] Fetching from:', url);
    console.log('[getUserOnePieceCardOwnerships] Headers:', headers);

    const response = await fetch(url, { headers });

    console.log(
      '[getUserOnePieceCardOwnerships] Response status:',
      response.status,
    );
    console.log('[getUserOnePieceCardOwnerships] Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '[getUserOnePieceCardOwnerships] Error response:',
        errorText,
      );
      throw new Error(
        `Failed to fetch One Piece card ownerships: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    const data = await response.json();
    console.log('[getUserOnePieceCardOwnerships] Response data:', data);
    console.log(
      '[getUserOnePieceCardOwnerships] Ownerships count:',
      data.ownerships?.length || 0,
    );

    return data.ownerships || [];
  } catch (error) {
    console.error('[getUserOnePieceCardOwnerships] Error:', error);
    throw error;
  }
}

export async function getUserOnePieceSetProgress(): Promise<
  Record<string, number>
> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/progress`,
      {
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to fetch One Piece set progress: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.progress || {};
  } catch (error) {
    console.error('Error fetching One Piece set progress:', error);
    throw error;
  }
}

export async function syncOnePieceSetProgress(): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/progress/sync`,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to sync One Piece set progress: ${response.statusText}`,
      );
    }
  } catch (error) {
    console.error('Error syncing One Piece set progress:', error);
    throw error;
  }
}

export async function getOnePieceCardOwnership(
  cardId: string,
): Promise<OnePieceCardOwnership | null> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/${cardId}`,
      {
        headers,
      },
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(
        `Failed to fetch One Piece card ownership: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.ownership || null;
  } catch (error) {
    console.error('Error fetching One Piece card ownership:', error);
    throw error;
  }
}

export async function upsertOnePieceCardOwnership(
  cardId: string,
  ownershipData: OnePieceCardOwnershipInput,
): Promise<OnePieceCardOwnership> {
  try {
    const headers = await getAuthHeaders();

    // Include setId in the request body if it exists in ownershipData
    const body = {
      ...ownershipData,
    };

    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/${cardId}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to update One Piece card ownership: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error updating One Piece card ownership:', error);
    throw error;
  }
}

export async function deleteOnePieceCardOwnership(
  cardId: string,
): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/${cardId}`,
      {
        method: 'DELETE',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to delete One Piece card ownership: ${response.statusText}`,
      );
    }
  } catch (error) {
    console.error('Error deleting One Piece card ownership:', error);
    throw error;
  }
}

export async function toggleOnePieceCardOwnership(
  cardId: string,
  setId: string,
  justTcgId?: string,
): Promise<OnePieceCardOwnership> {
  try {
    const headers = await getAuthHeaders();
    let url = `${API_BASE_URL}/api/onepiece/ownership/${cardId}/toggle?setId=${encodeURIComponent(setId)}`;
    if (justTcgId) {
        url += `&justTcgId=${encodeURIComponent(justTcgId)}`;
    }

    const response = await fetch(
      url,
      {
        method: 'POST',
        headers,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to toggle One Piece card ownership: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling One Piece card ownership:', error);
    throw error;
  }
}

/**
 * Toggle One Piece card wishlist status
 */
export async function toggleOnePieceCardWishlist(
  cardId: string,
  setId: string,
): Promise<OnePieceCardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/onepiece/ownership/${cardId}/wishlist/toggle?setId=${encodeURIComponent(setId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to toggle One Piece card wishlist: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling One Piece card wishlist:', error);
    throw error;
  }
}

/**
 * Toggle One Piece card favorite status
 */
export async function toggleOnePieceCardFavorite(
  cardId: string,
  setId: string,
): Promise<OnePieceCardOwnership> {
  try {
    const headers = await getAuthHeaders();
    const url = `${API_BASE_URL}/api/onepiece/ownership/${cardId}/favorite/toggle?setId=${encodeURIComponent(setId)}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(
        `Failed to toggle One Piece card favorite: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.ownership;
  } catch (error) {
    console.error('Error toggling One Piece card favorite:', error);
    throw error;
  }
}

export async function bulkUpsertOnePieceCardOwnerships(
  ownerships: Array<{ cardId: string } & OnePieceCardOwnershipInput>,
): Promise<OnePieceCardOwnership[]> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE_URL}/api/onepiece/ownership/bulk`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ ownerships }),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to bulk update One Piece card ownerships: ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data.results || [];
  } catch (error) {
    console.error('Error bulk updating One Piece card ownerships:', error);
    throw error;
  }
}

export function onePieceOwnershipsToMap(
  ownerships: OnePieceCardOwnership[],
): Record<string, OnePieceCardOwnership> {
  return ownerships.reduce(
    (map, ownership) => {
      map[ownership.cardId] = ownership;
      return map;
    },
    {} as Record<string, OnePieceCardOwnership>,
  );
}
